"use client"

import { useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from "react"
import { cn } from "@/lib/utils"

type Direction = "up" | "left" | "right" | "scale"

/**
 * Reveal — fades/slides its children into view as they scroll into the viewport.
 *
 * Pure IntersectionObserver + CSS (see `.reveal` utilities in app/globals.css):
 * - No animation library, no timers → static-export safe.
 * - Reveals once, then stops observing (content stays put).
 * - Elements already on screen at load animate in immediately.
 * - prefers-reduced-motion shows everything instantly (handled in CSS).
 *
 * Use `delay` (ms) on adjacent Reveals to stagger a group of cards.
 */
export function Reveal({
  children,
  className,
  direction = "up",
  delay = 0,
  as: Tag = "div",
  threshold = 0.15,
}: {
  children: ReactNode
  className?: string
  direction?: Direction
  /** Delay in ms — use increasing values on siblings to stagger a group. */
  delay?: number
  /** Element to render (default div). */
  as?: ElementType
  threshold?: number
}) {
  const ref = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Guard for environments without IntersectionObserver — just show it.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold, rootMargin: "0px 0px -8% 0px" },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold])

  const dirClass =
    direction === "left"
      ? "reveal-left"
      : direction === "right"
        ? "reveal-right"
        : direction === "scale"
          ? "reveal-scale"
          : ""

  return (
    <Tag
      ref={ref as never}
      className={cn("reveal", dirClass, visible && "is-visible", className)}
      style={delay ? ({ "--reveal-delay": `${delay}ms` } as CSSProperties) : undefined}
    >
      {children}
    </Tag>
  )
}
